import { useCallback } from "react";
import { usePublicMediaAssets } from "@/hooks/usePublicMediaAssets";
import { useOrganization } from "hooks/useOrganization";
import { BrandKitAsset } from "pages/media/types";
import { FontPurpose, FontPurposes } from "../types";
import { useHandleChangeFont } from "./useHandleChangeFont";

export const useEnsureBrandKitDefaultFonts = () => {
  const { handleChangeFont } = useHandleChangeFont();
  const { defaultPublicFont, defaultArabicFont } = usePublicMediaAssets();
  const { organizationPreferences } = useOrganization({});

  const ensureDefaultFonts = useCallback(
    async (
      brandKitId: string | undefined,
      organizationId: string | undefined,
      currentFonts: Partial<Record<FontPurpose, BrandKitAsset | undefined>>
    ): Promise<BrandKitAsset[]> => {
      if (!brandKitId || !organizationId || !defaultPublicFont) return [];

      const defaultFont =
        organizationPreferences?.language === "ar-AE" && defaultArabicFont
          ? defaultArabicFont
          : defaultPublicFont;

      const missingPurposes = FontPurposes.filter(
        (purpose) => !currentFonts[purpose]
      );

      const defaultFontId = defaultFont.id;
      if (!missingPurposes.length || !defaultFontId) return [];

      await Promise.all(
        missingPurposes.map((purpose) =>
          handleChangeFont(purpose, defaultFontId, organizationId, brandKitId)
        )
      );

      return missingPurposes.map((purpose) => ({
        asset_id: defaultFontId,
        purpose,
        path: defaultFont.path ?? "",
        name: defaultFont.description ?? "",
        type: "Font",
      }));
    },
    [
      handleChangeFont,
      defaultPublicFont,
      defaultArabicFont,
      organizationPreferences?.language,
    ]
  );

  return { ensureDefaultFonts };
};
